// OAuth 流程在前端 SPA 的落地点。provider 回调到后端后，
// 后端会再 302 到这里；前端只负责展示成功/失败状态，
// 并通过调用 /auth/session 重新同步 session store。
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { readSession } from './api';
import { useSessionStore } from '../store/sessionStore';
import { pushNotification } from '../store/notificationStore';

const ERROR_MESSAGES: Record<string, string> = {
  oauth_single_user_conflict: '当前系统仅支持一个管理员账号，且已绑定其它身份。',
  oauth_state_mismatch: '授权链路失效，请重新发起登录。',
  access_denied: '已取消授权。',
  bad_request: '授权回调参数缺失，请重试。',
};

export function OAuthCallback() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { provider } = useParams<{ provider: string }>();
  const setAuthenticated = useSessionStore((s) => s.setAuthenticated);
  const setAnonymous = useSessionStore((s) => s.setAnonymous);
  const [status, setStatus] = useState<'loading' | 'error'>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    const query = params.get('status');
    const code = params.get('code');
    const from = params.get('from') ?? '/';

    if (query === 'error') {
      const friendly =
        (code && ERROR_MESSAGES[code]) ?? '授权失败，请稍后重试。';
      pushNotification({
        message: friendly,
        level: 'danger',
        key: `oauth-error-${code ?? 'unknown'}`,
      });
      setErrorMsg(friendly);
      setStatus('error');
      return;
    }

    // status=success（或缺失时按成功处理）后，仍然要向服务端确认一次。
    let cancelled = false;
    readSession()
      .then((body) => {
        if (cancelled) return;
        if (body) {
          setAuthenticated(body.user, body.csrf_token);
          navigate(from, { replace: true });
        } else {
          setAnonymous();
          navigate('/login', { replace: true });
        }
      })
      .catch(() => {
        if (cancelled) return;
        pushNotification({
          message: '无法确认登录状态，请重试。',
          level: 'danger',
          key: 'oauth-session-check-failure',
        });
        setErrorMsg('无法确认登录状态，请重试。');
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [navigate, params, setAnonymous, setAuthenticated]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface text-foreground">
      <div className="w-full max-w-md rounded-lg border border-border bg-surface-muted p-6 text-center">
        <h1 className="text-lg font-semibold">
          {status === 'loading' ? `正在完成 ${provider ?? ''} 登录…` : '授权未能完成'}
        </h1>
        {status === 'error' && (
          <>
            <p
              role="alert"
              data-testid="oauth-error-message"
              className="mt-2 text-sm text-foreground-muted"
            >
              {errorMsg}
            </p>
            <button
              type="button"
              className="mt-4 rounded bg-primary px-3 py-2 text-sm text-primary-foreground"
              onClick={() => navigate('/login', { replace: true })}
            >
              返回登录
            </button>
          </>
        )}
      </div>
    </div>
  );
}
